import { AbstractProduct, Product } from 'src/store/design/types';

const MUG_PRINT_AREA_ATTRIBUTE = 'OpMugPrintArea';
const WRAP_AROUND_PRINT_AREA = 'wrap-around';
// Wrap-around variants follow the internalUid convention `<base>_wrap`. The leading
// underscore anchors the match so it can't collide with an unrelated "wrap" substring.
const WRAP_AROUND_INTERNAL_UID = '_wrap';

const isWrapAroundByInternalUid = (product: Product): boolean => product.internalUid.includes(WRAP_AROUND_INTERNAL_UID);

const isWrapAroundByAttribute = (product: Product): boolean => (
  product.attributes.some((attribute) => attribute.typeName === MUG_PRINT_AREA_ATTRIBUTE && attribute.value === WRAP_AROUND_PRINT_AREA)
);

/**
 * Mugs must always be produced wrap-around.
 *
 * The print-area variants (simple-photo / simple-photo-left / wrap-around) share the
 * SAME productUid and differ only by internalUid and the OpMugPrintArea attribute.
 * Resolving a bound product by productUid alone therefore lands on the first
 * (simple-photo) variant, so the design is produced and priced one-side even though
 * the customer sees a wrap-around design.
 *
 * Given the abstract product and the productUid a design is bound to, return the
 * wrap-around sibling variant if one exists. The internalUid convention (`_wrap`) is
 * the canonical key and takes priority; the OpMugPrintArea attribute is a fallback for
 * variants that don't follow the naming convention. Products with no wrap-around
 * sibling (e.g. water bottle / travel mug, which only expose a "default" print area,
 * and any non-mug product) return undefined and are left untouched.
 */
const findMugWrapAroundProduct = (
  abstractProduct: AbstractProduct | undefined,
  productUid: string | undefined,
): Product | undefined => {
  if (!abstractProduct || !productUid) {
    return undefined;
  }

  const siblings = abstractProduct.products.filter((product) => product.productUid === productUid);

  // internalUid convention takes priority; fall back to the semantic attribute.
  return siblings.find(isWrapAroundByInternalUid) ?? siblings.find(isWrapAroundByAttribute);
};

export default findMugWrapAroundProduct;
